import * as React from 'react'

import MuiAppBar from '@mui/material/AppBar'
import IconButton from '@mui/material/IconButton'
import Toolbar from '@mui/material/Toolbar'
import Grid from '@mui/material/Grid'

import PageTitle from './PageTitle'
import AppName from './AppName'
import SearchHeader from './SearchHeader'

import MenuIcon from '@mui/icons-material/Menu'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'

import usePageInfo from '../../hooks/usePageInfo'
import Drawer from '../Drawer'
import AccountMenu from '../AccountMenu'
import WorkflowStepper from '../WorkflowStepper'

import { UserContext } from '../../UserContext'
import {
    useContextsStore,
    selectIsPublicView,
} from '../../stores/useContextsStore'

import { getComparedContexts } from '../../routes'

import { sendToAnalytics } from '../../Analytics'
import {
    isGraphOnlyMode,
    isHideAppBar,
} from '../LeftMenuMigration/legacyBridge'

export default function AppBar({ onToggle }) {
    const [open, setOpen] = React.useState(false)
    // Read once at mount (the flags are immutable for the page's lifetime) —
    // same pattern as GraphToolsBar / PanelToggles.
    const [graphOnly] = React.useState(() => isGraphOnlyMode())
    const [hideAppBar] = React.useState(() => isHideAppBar())

    const { label, location } = usePageInfo()
    const comparedContexts = getComparedContexts()
    const isComparingContexts =
        location === 'context' && comparedContexts.length > 0

    const { username } = React.useContext(UserContext)
    const isPublic = useContextsStore(selectIsPublicView)

    // Graph-only embeds (?hide_max=1) get no header chrome at all;
    // ?hide_app_bar=1 removes just this header (other chrome params stay
    // independent). The Drawer is rendered from here, so it goes too.
    if (graphOnly || hideAppBar) return null

    const editableLabel =
        location === 'context' && comparedContexts.length === 0 && !isPublic

    const finalPageLabel = isComparingContexts
        ? createFinalContextLabel(label, comparedContexts)
        : label

    const toggleDrawer = () => {
        setOpen(!open)
        onToggle && onToggle(!open)
        sendToAnalytics({
            userId: username,
            event: 'toggle_drawer',
            data: { open: !open },
        })
    }

    const stepper = 'basic'

    const onTitleChange = (oldLabel, newLabel) => {
        sendToAnalytics({
            userId: username,
            event: 'change_graph_label',
            data: { oldLabel, newLabel },
        })
    }

    const onWorkflowClick = stepperType => {
        sendToAnalytics({
            userId: username,
            event: 'workflow_click',
            data: { type: stepperType },
        })
    }

    const onAccountMenuClick = () => {
        sendToAnalytics({
            userId: username,
            event: 'account_menu_click',
            data: { source: location },
        })
    }
    return (
        <>
            <MuiAppBar
                position="fixed"
                open={open}
                elevation={0}
                sx={{
                    zIndex: theme => theme.zIndex.drawer + 1,
                    minHeight: theme => theme.spacing(6),
                    // Match the graph canvas backdrop (--graph-background,
                    // defined in stylesheets/style.css per theme) so the bar
                    // blends with the graph container. Pages that don't load
                    // style.css (e.g. apps.ejs) fall back to the MUI default.
                    bgcolor: theme =>
                        `var(--graph-background, ${theme.palette.background.default})`,
                    ml: 0,
                    transition: theme =>
                        theme.transitions.create(['width', 'margin'], {
                            easing: theme.transitions.easing.sharp,
                            duration: theme.transitions.duration.leavingScreen,
                        }),
                }}
            >
                <Toolbar
                    disableGutters
                    sx={{
                        alignItems: 'center',
                        paddingRight: theme => theme.spacing(1),
                    }}
                    variant="dense"
                >
                    <IconButton
                        aria-label="open drawer"
                        onClick={toggleDrawer}
                        sx={{
                            width: theme => theme.spacing(6),
                            height: theme => theme.spacing(6),
                            backgroundColor: theme => 'rgba(100,100,100,0.7)',
                            borderRadius: 0,
                        }}
                    >
                        {open ? (
                            <ChevronLeftIcon
                                sx={{
                                    color: theme => theme.palette.common.white,
                                }}
                            />
                        ) : (
                            <MenuIcon
                                sx={{
                                    color: theme => theme.palette.common.white,
                                }}
                            />
                        )}
                    </IconButton>
                    {location === 'search' ? (
                        <SearchHeader
                            pageLabel={finalPageLabel}
                            onAccountMenuClick={onAccountMenuClick}
                        />
                    ) : (
                        <>
                            <Grid container justifyContent="flex-start">
                                <PageTitle
                                    label={finalPageLabel}
                                    editable={editableLabel}
                                    onTitleChange={(oldLabel, newLabel) =>
                                        onTitleChange(oldLabel, newLabel)
                                    }
                                />
                            </Grid>
                            <Grid
                                container
                                justifyContent="flex-end"
                                alignItems="center"
                            >
                                <AppName />
                                <AccountMenu
                                    onAccountMenuClick={onAccountMenuClick}
                                />
                                {(location == 'apps' ||
                                    location == 'context' ||
                                    location == 'synthesis' ||
                                    location == 'conceptsSynthesis') && (
                                    <WorkflowStepper
                                        stepperName={
                                            location == 'apps'
                                                ? 'apps'
                                                : stepper
                                        }
                                        onWorkflowClick={stepperType =>
                                            onWorkflowClick(stepperType)
                                        }
                                        location={location}
                                    />
                                )}
                            </Grid>
                        </>
                    )}
                </Toolbar>
            </MuiAppBar>
            <Drawer open={open} onClose={toggleDrawer} />
        </>
    )
}

const createFinalContextLabel = (sourceContext, comparedContexts) => {
    if (comparedContexts.length <= 2)
        return `${sourceContext} to ${comparedContexts.join(', ')}`

    return `${sourceContext} to others`
}
